import { api } from '@/config/api';
import { tokenStorage } from '@/utils/jwt';

// ─── Types ────────────────────────────────────────────────────────────────────

export type NotificationType =
  | 'lead_assigned'
  | 'lead_handed_off'
  | 'lead_status_changed'
  | 'lead_followup_due'
  | 'campaign_completed'
  | 'template_approved'
  | 'template_rejected'
  | 'ai_max_turns_exceeded'
  | 'whatsapp_inbound';

export interface AppNotification {
  _id: string;
  notificationUid: string;
  companyUid: string;
  recipientUserUid: string;
  type: NotificationType;
  title: string;
  body?: string;
  data: Record<string, unknown>;
  readAt?: string;
  acknowledged: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ListNotificationsResponse {
  items: AppNotification[];
  total: number;
  page: number;
  limit: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const notificationsService = {
  list: async (params: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
  } = {}): Promise<ListNotificationsResponse> =>
    (await api.get('/slighter_crm/notifications', { params })).data,

  unreadCount: async (): Promise<{ count: number }> =>
    (await api.get('/slighter_crm/notifications/unread-count')).data,

  markAsRead: async (uid: string): Promise<AppNotification> =>
    (await api.post(`/slighter_crm/notifications/${uid}/read`)).data,

  markAllAsRead: async (): Promise<{ updated: number }> =>
    (await api.post('/slighter_crm/notifications/read-all')).data,

  /**
   * Opens an SSE stream for real-time notifications.
   * EventSource cannot send Authorization headers, so the JWT is passed as
   * ?access_token=... — the JwtStrategy accepts it from header OR query.
   */
  openStream: (): EventSource | null => {
    const token = tokenStorage.get();
    if (!token) return null;
    const base = process.env.REACT_APP_API_BASE ?? 'http://localhost:3001/api/v1';
    const url = `${base}/slighter_crm/notifications/stream?access_token=${encodeURIComponent(token)}`;
    return new EventSource(url);
  },
};
